import React, { createContext, useContext, useState, ReactNode } from 'react'
import { ControlledModal } from '../../layouts/components/modal'
import Iframe from 'react-iframe'
import { Button, ButtonStyle } from '../../components/simple/button'
import { useTranslation } from '../../providers/translation'
import { useAppContext, User } from '../../providers/context'
import { makeRequest, showAxiosErrorAsToast } from '../../request'

interface TermsOfServiceContextType {
  requireTermsOfService: (user: User) => Promise<boolean>
}

const TermsOfServiceContext = createContext<TermsOfServiceContextType | undefined>(undefined)

export const TermsOfServiceProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [resolvePromise, setResolvePromise] = useState<(value: boolean) => void>(() => () => {})
  const { setState } = useAppContext()
  const requireTermsOfService = (user: User) => {
    if (user.acceptedTermsOfService) return Promise.resolve(true)
    setIsModalOpen(true)
    return new Promise<boolean>((resolve) => {
      const resolveWrapper = (accepted: boolean) => {
        resolve(accepted)
        if (accepted && user) {
          makeRequest('/frontend_api/markOnboardingComplete', { onboarding: 'termsOfService' }).catch((e) =>
            showAxiosErrorAsToast(e, 'Failed to accept terms of service')
          )
          setState((state) => ({ ...state, user: state.user && { ...state.user, acceptedTermsOfService: true } }))
        }
      }
      setResolvePromise(() => resolveWrapper)
    })
  }

  const { tr } = useTranslation()

  return (
    <TermsOfServiceContext.Provider value={{ requireTermsOfService }}>
      {children}
      <ControlledModal
        open={isModalOpen}
        title="Please Read Our Terms Of Service"
        onOpenChange={(o) => {
          if (!isModalOpen && !o) return
          if (!o) resolvePromise(false)
          setIsModalOpen(o)
        }}
      >
        <Iframe url="/terms-and-conditions/raw" frameBorder={0} styles={{ width: '100%', height: '50vh' }} />
        <div className="mt-3 flex flex-wrap justify-between gap-2">
          <Button
            onClick={() => {
              resolvePromise(true)
              setIsModalOpen(false)
            }}
            buttonStyle={ButtonStyle.blue}
          >
            {tr('I have read and agree to the terms of service')}
          </Button>
          <Button
            onClick={() => {
              resolvePromise(false)
              setIsModalOpen(false)
            }}
          >
            {tr('I do not agree')}
          </Button>
        </div>
      </ControlledModal>
    </TermsOfServiceContext.Provider>
  )
}

export const useTermsOfService = () => {
  const context = useContext(TermsOfServiceContext)
  if (context === undefined) {
    throw new Error('useTermsOfService must be used within a TermsOfServiceProvider')
  }
  return context.requireTermsOfService
}
